import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useCapacityStore } from './store/CapacityPlannerContext';
import { GLOBAL_DASHBOARD_ID } from './store/CapacityPlannerStore';
import CPDashboard from './components/CPDashboard';
import CPCOSummary from './components/CPCOSummary';
import CPCalculationTab from './components/CPCalculationTab';
import CPForecastTab from './components/CPForecastTab';
import CPInRunCrewingTab from './components/CPInRunCrewingTab';
import CPChangeoverCrewingTab from './components/CPChangeoverCrewingTab';
import CPReferenceTables from './components/CPReferenceTables';
import CPJobOverlay from './components/CPJobOverlay';
import CPVersionBar from './components/CPVersionBar';
import CPDashboardListing from './components/CPDashboardListing';
import styles from './capacityPlannerScreen.module.scss';

const CapacityPlannerScreen = observer(() => {
  const store = useCapacityStore();
  const [showCreate, setShowCreate] = useState(false);
  const [draftName, setDraftName]   = useState('');

  const globalRecord  = store.dashboardRecords.find((d) => d.id === GLOBAL_DASHBOARD_ID);
  const privateRecords = store.dashboardRecords.filter(
    (d) => d.id !== GLOBAL_DASHBOARD_ID && !d.isPublished,
  );
  const sharedRecords = store.dashboardRecords.filter(
    (d) => d.id !== GLOBAL_DASHBOARD_ID && d.isPublished,
  );

  // Auto-load the global dashboard on first mount
  useEffect(() => {
    store.openDashboard(GLOBAL_DASHBOARD_ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    if (!draftName.trim()) return;
    store.createPrivateDashboard(draftName.trim());
    setShowCreate(false);
    setDraftName('');
  };

  return (
    <div className={styles.layout}>
      <div className={styles.demoBanner}>
        <i className="pi pi-info-circle" /> DEMO MODE — Mock data only. No live database connections.
      </div>
      <div className={styles.container}>

        {/* ================================================================
            Sidebar
        ================================================================= */}
        <nav className={styles.sidebar}>
          <div className={styles.sidebarTitle}>Capacity Model</div>

          {/* Workspaces landing */}
          <button
            type="button"
            className={`${styles.navItem} ${store.activeView === 'listing' ? styles.navItemActive : ''}`}
            onClick={() => store.backToLanding()}
          >
            <i className="pi pi-th-large" />
            <span className={styles.navItemText}>Workspaces</span>
          </button>

          <div className={styles.sidebarDivider} />

          {/* ---- Global Dashboard ---- */}
          <div className={styles.sidebarGroupLabel}>
            <i className="pi pi-globe" /> Global
          </div>
          {globalRecord && (
            <button
              type="button"
              className={`${styles.navItem} ${store.activeDashboardId === globalRecord.id && store.activeView === 'dashboard' ? styles.navItemActive : ''}`}
              onClick={() => {
                store.setActiveView('dashboard');
                store.openDashboard(globalRecord.id);
              }}
              title={globalRecord.description}
            >
              <i className="pi pi-chart-bar" />
              <span className={styles.navItemText}>{globalRecord.name}</span>
              <span className={styles.publicPill}>PUBLIC</span>
            </button>
          )}

          {/* ---- My Dashboards ---- */}
          <div className={styles.sidebarGroupLabel} style={{ marginTop: '0.5rem' }}>
            <i className="pi pi-lock" /> My Dashboards
          </div>

          {/* Shared (published private) dashboards */}
          {sharedRecords.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`${styles.navItem} ${store.activeDashboardId === d.id && store.activeView === 'dashboard' ? styles.navItemActive : ''}`}
              onClick={() => { store.setActiveView('dashboard'); store.openDashboard(d.id); }}
              title={d.description}
            >
              <i className="pi pi-share-alt" />
              <span className={styles.navItemText}>{d.name}</span>
            </button>
          ))}

          {/* Private dashboards */}
          {privateRecords.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`${styles.navItem} ${store.activeDashboardId === d.id && store.activeView === 'dashboard' ? styles.navItemActive : ''}`}
              onClick={() => { store.setActiveView('dashboard'); store.openDashboard(d.id); }}
              title={d.description}
            >
              <i className="pi pi-file-edit" />
              <span className={styles.navItemText}>{d.name}</span>
            </button>
          ))}

          <button
            type="button"
            className={styles.newDashBtn}
            onClick={() => { setDraftName(''); setShowCreate(true); }}
          >
            <i className="pi pi-plus" /> New Dashboard
          </button>

          <div className={styles.sidebarDivider} />

          {/* Reference Tables */}
          <button
            type="button"
            className={`${styles.navItem} ${store.activeView === 'reference' ? styles.navItemActive : ''}`}
            onClick={() => store.setActiveView('reference')}
          >
            <i className="pi pi-list" /> Reference Tables
          </button>
        </nav>

        {/* ================================================================
            Main content
        ================================================================= */}
        <main className={styles.content}>
          <CPJobOverlay />

          {/* Workspace listing */}
          {store.activeView === 'listing' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <CPDashboardListing onCreateClick={() => { setDraftName(''); setShowCreate(true); }} />
            </div>
          )}

          {/* Dashboard workspace */}
          {store.activeView === 'dashboard' && store.activeDashboardId !== null && (
            <>
              <CPVersionBar />
              <TabView className={styles.dashboardTabView}>
                <TabPanel header="Dashboard" leftIcon="pi pi-chart-bar mr-2">
                  <CPDashboard />
                </TabPanel>
                <TabPanel header="CO Summary" leftIcon="pi pi-sync mr-2">
                  <CPCOSummary />
                </TabPanel>
                <TabPanel header="ResPlanCalc" leftIcon="pi pi-calculator mr-2">
                  <CPCalculationTab />
                </TabPanel>
                <TabPanel header="Forecast" leftIcon="pi pi-table mr-2">
                  <CPForecastTab />
                </TabPanel>
                <TabPanel header="In-Run Crewing" leftIcon="pi pi-users mr-2">
                  <CPInRunCrewingTab />
                </TabPanel>
                <TabPanel header="Changeover Crewing" leftIcon="pi pi-refresh mr-2">
                  <CPChangeoverCrewingTab />
                </TabPanel>
              </TabView>
            </>
          )}

          {/* Reference Tables */}
          {store.activeView === 'reference' && <CPReferenceTables />}
        </main>
      </div>

      {/* ================================================================
          Create dashboard dialog
      ================================================================= */}
      <Dialog
        header="Create New Dashboard"
        visible={showCreate}
        onHide={() => setShowCreate(false)}
        style={{ width: 360 }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button label="Cancel" severity="secondary" text size="small" onClick={() => setShowCreate(false)} />
            <Button
              label="Create"
              icon="pi pi-plus"
              size="small"
              style={{ background: '#522d6e', borderColor: '#522d6e' }}
              disabled={!draftName.trim()}
              onClick={handleCreate}
            />
          </div>
        }
        modal
      >
        <div style={{ padding: '0.25rem 0 0.5rem' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>
            Dashboard Name
          </label>
          <InputText
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="e.g. Q3 Scenario Analysis"
            style={{ width: '100%', fontSize: 13 }}
            autoFocus
          />
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#94a3b8' }}>
            Private to you until published.
          </p>
        </div>
      </Dialog>
    </div>
  );
});

export default CapacityPlannerScreen;
